import { Disclosure } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { OrientedUrl, urlIndexMap } from "@/data/constants/oriented";
import { useSymbolInfo } from "@/features/analysis/services/symbol-info";
import { useLang } from "@/hooks/use-lang";
import { usePath } from "@/hooks/use-path";

import { getNavigationList } from "../../data/navigation-list";

type Props = {
  symbol: string;
  onLinkClick: () => void;
};

const isValidOrientedUrl = (url: string): url is OrientedUrl =>
  url in urlIndexMap;

export const NavigationClosure = ({ symbol, onLinkClick }: Props) => {
  const { lang, t } = useLang();
  const { lastRoute } = usePath();
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const { data: symbolInfo, isLoading, isError } = useSymbolInfo(symbol);
  const list = getNavigationList({
    lang,
    t,
    symbol,
    country: symbolInfo?.country ?? "US",
  });

  const urlIndex = isValidOrientedUrl(lastRoute)
    ? urlIndexMap[lastRoute] + 1
    : 0;
  const [closureIndex, setClosureIndex] = useState(urlIndex);

  return isLoading || isError ? null : (
    <ul>
      {list.map(({ key, title, pageLink, subLinks }, index) => (
        <Disclosure as="li" key={title}>
          {() =>
            key !== "overview" && !symbolInfo?.[key] ? (
              <Disclosure.Button
                as="button"
                className="pointer-events-none flex w-full cursor-not-allowed items-center gap-2 py-2 font-semibold opacity-30"
              >
                <ChevronRightIcon className="h-4 w-4 shrink-0" />
                {title}
              </Disclosure.Button>
            ) : (
              <>
                <Disclosure.Button
                  className="flex w-full items-center gap-2 py-2 font-semibold"
                  onClick={() => setClosureIndex(index)}
                  aria-label={`open ${key} page closure`}
                >
                  <ChevronRightIcon
                    className={twMerge(
                      "h-4 w-4 shrink-0",
                      closureIndex === index && "rotate-90 transform"
                    )}
                  />
                  {title}
                </Disclosure.Button>
                <Disclosure.Panel
                  as="ul"
                  className={twMerge(
                    "py-1 text-start",
                    closureIndex !== index && "hidden"
                  )}
                  static
                >
                  {subLinks.map(({ name, anchor }) => (
                    <Link
                      href={`${pageLink}#${anchor}`}
                      key={name}
                      aria-label={`go to ${key}-${anchor} section`}
                      onClick={() => {
                        onLinkClick();
                      }}
                      aria-current={hash === `#${anchor}`}
                    >
                      <li
                        className={twMerge(
                          "py-1 pl-6 font-medium text-zinc-500 transition-colors hover:bg-zinc-200/30 hover:text-sky-700",
                          urlIndex === index &&
                            hash === `#${anchor}` &&
                            "bg-zinc-200/30 text-sky-700"
                        )}
                      >
                        {name}
                      </li>
                    </Link>
                  ))}
                </Disclosure.Panel>
              </>
            )
          }
        </Disclosure>
      ))}
    </ul>
  );
};
